import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import HealthScore from "@/pages/tools/HealthScore";
import { TOOLS } from "@/pages/tools/toolkit";
import {
  Search, Sparkles, ArrowRight, Download, Clock, Tag, BookOpen, Plus,
  FileText, ClipboardList, ShieldCheck, Layers, Sigma, Grid3x3, HardHat, Wrench, Cog,
  Target, AlertTriangle, ClipboardCheck, BadgeCheck, Printer, Package, Scissors, Beaker,
  Warehouse, Truck, Boxes, Users, GraduationCap, Star, Bot, TrendingUp, Megaphone,
  CheckCircle2, Send,
} from "lucide-react";

const ICONS = {
  FileText, ClipboardList, ShieldCheck, Layers, Sigma, Grid3x3, HardHat, Wrench, Cog,
  Target, AlertTriangle, ClipboardCheck, BadgeCheck, Printer, Package, Scissors, Beaker,
  Warehouse, Truck, Boxes, Users, GraduationCap, Star, Bot, TrendingUp, Megaphone,
};

const SKILL_COLORS = {
  Beginner: "bg-emerald-100 text-emerald-800",
  Intermediate: "bg-blue-100 text-blue-800",
  Advanced: "bg-amber-100 text-amber-800",
};

export default function Library() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const q = params.get("q") || "";
  const cat = params.get("category") || "";
  const type = params.get("type") || "";

  useEffect(() => {
    api.get("/public/library/categories").then(({ data }) => {
      setCategories(data.categories);
      setTotal(data.total);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (cat) p.category = cat;
    if (type) p.resource_type = type;
    api.get("/public/library", { params: p }).then(({ data }) => {
      setResources(data.results);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [q, cat, type]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const featured = useMemo(() => resources.slice(0, 6), [resources]);
  const rest = useMemo(() => resources.slice(6), [resources]);

  // ---- Suggest a template (community submissions) ----
  const RESOURCE_TYPES = ["SOP Template", "Work Instruction", "Form", "Checklist", "Excel Calculator", "KPI Dashboard", "Visual Standard", "Audit Form", "Training Document", "AI Prompt Pack", "Case Study", "Best Practice"];
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestForm, setSuggestForm] = useState({
    title: "", description: "", category: "", resource_type: "",
    why_useful: "", submitter_email: "", submitter_name: "", submitter_company: "", external_url: "",
  });
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestDone, setSuggestDone] = useState(false);

  const submitSuggestion = async (e) => {
    if (e) e.preventDefault();
    const f = suggestForm;
    if (f.title.trim().length < 4) { toast.error("Title too short"); return; }
    if (f.description.trim().length < 20) { toast.error("Description too short (min 20 chars)"); return; }
    if (!f.category || !f.resource_type) { toast.error("Pick a category and resource type"); return; }
    if (!/^\S+@\S+\.\S+$/.test(f.submitter_email)) { toast.error("Valid email required"); return; }
    setSuggestLoading(true);
    try {
      await api.post("/public/library/suggest", f);
      setSuggestDone(true);
      toast.success("Thanks! Scott reviews every submission personally.");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit"); }
    finally { setSuggestLoading(false); }
  };

  const closeSuggest = () => {
    setSuggestOpen(false);
    setTimeout(() => {
      setSuggestDone(false);
      setSuggestForm({ title: "", description: "", category: "", resource_type: "", why_useful: "", submitter_email: "", submitter_name: "", submitter_company: "", external_url: "" });
    }, 300);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight">
            Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm hover:text-primary hidden md:inline">Home</Link>
            <Link to="/#live-demo" className="text-sm hover:text-primary hidden md:inline">AI Demo</Link>
            <Link to="/login"><Button variant="ghost" size="sm" className="rounded-sm">Sign in</Button></Link>
            <Link to="/register"><Button size="sm" className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="library-nav-register">Free account</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="max-w-4xl">
            <div className="overline text-accent mb-4">◉ FREE · NO CREDIT CARD</div>
            <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[1.05]" data-testid="library-hero-title">
              The Free<br/>
              <span className="text-primary">Manufacturing Excellence</span><br/>
              Library.
            </h1>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed max-w-2xl">
              A growing collection of professional SOPs, templates, calculators, checklists, guides,
              and AI-powered tools used to improve quality, productivity, safety, and operational excellence.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/tools">
                <Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 text-white h-12 px-6" data-testid="library-cta-tools">
                  Try 6 free interactive tools <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#browse">
                <Button size="lg" className="rounded-sm bg-accent hover:bg-accent/90 text-white h-12 px-6" data-testid="library-cta-browse">
                  Browse free resources <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Link to="/register">
                <Button size="lg" variant="outline" className="rounded-sm h-12 px-6 border-foreground/20" data-testid="library-cta-account">
                  Create your free account
                </Button>
              </Link>
              <Button
                size="lg"
                variant="ghost"
                onClick={() => setSuggestOpen(true)}
                className="rounded-sm h-12 px-6 text-primary hover:bg-primary/5"
                data-testid="library-cta-suggest"
              >
                <Plus className="w-4 h-4 mr-2" /> Suggest a template
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
              <span>◆ {total} resources</span>
              <span>◆ {categories.filter(c => c.count > 0).length} active categories</span>
              <span>◆ Zero spam</span>
              <span>◆ Editable via AI in RuleAI</span>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive tools + health score */}
      <section className="border-b border-border bg-zinc-950" data-testid="library-tools-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-6">
          <HealthScore />
          <div className="dark grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {TOOLS.map((t) => (
              <Link key={t.key} to={`/tools/${t.slug}`} className="border border-border bg-card text-foreground rounded-sm p-4 hover:border-primary transition-colors" data-testid={`library-tool-${t.key}`}>
                <t.icon className="w-5 h-5 text-primary" />
                <div className="font-semibold text-sm mt-3">{t.title}</div>
                <div className="text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground mt-1">{t.minutes} · interactive</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="border-b border-border sticky top-14 z-30 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search SOPs, calculators, checklists, guides…"
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              className="pl-11 h-12 rounded-sm text-base"
              data-testid="library-search"
            />
            {(cat || type || q) && (
              <button
                onClick={() => setParams({})}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground"
                data-testid="library-clear-filters"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="browse" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="overline text-primary">/ CATEGORIES</div>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-2">Browse by topic</h2>
            </div>
            {cat && (
              <button onClick={() => setParam("category", "")} className="text-xs font-mono-plex uppercase tracking-widest text-primary hover:underline">
                Clear category filter
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="library-categories">
            {categories.map((c) => {
              const Icon = ICONS[c.icon] || Tag;
              const active = cat === c.slug;
              return (
                <button
                  key={c.slug}
                  onClick={() => setParam("category", active ? "" : c.slug)}
                  className={`text-left p-4 rounded-sm border transition-all ${
                    active
                      ? "border-primary bg-primary/5 shadow-sm"
                      : c.count === 0
                      ? "border-border bg-card/40 opacity-60"
                      : "border-border bg-card hover:border-primary hover:shadow-sm"
                  }`}
                  data-testid={`library-cat-${c.slug}`}
                >
                  <Icon className={`w-4 h-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
                  <div className="mt-2 font-medium text-sm leading-tight">{c.name}</div>
                  <div className="mt-1 text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground">
                    {c.count} {c.count === 1 ? "resource" : "resources"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Results */}
      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">Loading resources…</div>
          ) : resources.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
              <BookOpen className="w-8 h-8 mx-auto text-muted-foreground" />
              <div className="font-display text-lg mt-4">No resources match those filters</div>
              <p className="text-sm text-muted-foreground mt-2">Try clearing the filters or searching a different term.</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {!q && !cat && !type && featured.length > 0 && (
                <>
                  <div className="mb-6 overline text-primary">/ FEATURED · MOST DOWNLOADED</div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
                    {featured.map((r) => <ResourceCard key={r.slug} r={r} large />)}
                  </div>
                </>
              )}

              <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div className="overline text-muted-foreground">
                  {q || cat || type ? "/ SEARCH RESULTS" : "/ ALL RESOURCES"} · {resources.length}
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(q || cat || type ? resources : rest).map((r) => <ResourceCard key={r.slug} r={r} />)}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-zinc-950 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 text-center">
          <div className="overline text-accent mb-4">/ UNLOCK AI CUSTOMIZATION</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
            Every template above &mdash; <span className="text-accent">editable by AI</span> in RuleAI.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
            Sign up free during beta. Open any template in the RuleAI workspace and use AI to rewrite it for your company, convert it to an SOP, translate it, or export to Word, PDF, or Excel.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register">
              <Button size="lg" className="rounded-sm bg-accent hover:bg-accent/90 text-white h-12 px-6" data-testid="library-footer-cta">
                Create free account <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Suggest a template dialog */}
      <Dialog open={suggestOpen} onOpenChange={(v) => { if (!v) closeSuggest(); else setSuggestOpen(true); }}>
        <DialogContent className="sm:max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="suggest-dialog">
          {!suggestDone ? (
            <>
              <DialogHeader>
                <div className="overline text-accent">◉ COMMUNITY · MODERATED</div>
                <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                  Suggest a template
                </DialogTitle>
                <p className="text-sm text-muted-foreground pt-1">
                  What&rsquo;s missing? Every submission is reviewed personally by Scott. Approved templates get added
                  with credit — and you get a founding-member badge on your account.
                </p>
              </DialogHeader>
              <form onSubmit={submitSuggestion} className="space-y-3 pt-3">
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">Template title *</label>
                  <Input
                    value={suggestForm.title}
                    onChange={(e) => setSuggestForm({ ...suggestForm, title: e.target.value })}
                    placeholder="e.g. First Article Inspection form for CNC parts"
                    className="rounded-sm"
                    data-testid="suggest-title"
                    maxLength={200}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Category *</label>
                    <Select value={suggestForm.category} onValueChange={(v) => setSuggestForm({ ...suggestForm, category: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="suggest-category"><SelectValue placeholder="Choose one" /></SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Resource type *</label>
                    <Select value={suggestForm.resource_type} onValueChange={(v) => setSuggestForm({ ...suggestForm, resource_type: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="suggest-type"><SelectValue placeholder="Choose one" /></SelectTrigger>
                      <SelectContent>
                        {RESOURCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">Description *</label>
                  <Textarea
                    rows={3}
                    value={suggestForm.description}
                    onChange={(e) => setSuggestForm({ ...suggestForm, description: e.target.value })}
                    placeholder="What does the template do? Who uses it? What sections/fields does it have?"
                    className="rounded-sm resize-none"
                    data-testid="suggest-description"
                    maxLength={2000}
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">Why is this useful?</label>
                  <Textarea
                    rows={2}
                    value={suggestForm.why_useful}
                    onChange={(e) => setSuggestForm({ ...suggestForm, why_useful: e.target.value })}
                    placeholder="What problem does it solve? How much time does it save?"
                    className="rounded-sm resize-none"
                    data-testid="suggest-why"
                    maxLength={1000}
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">Link to your version (optional)</label>
                  <Input
                    value={suggestForm.external_url}
                    onChange={(e) => setSuggestForm({ ...suggestForm, external_url: e.target.value })}
                    placeholder="https://drive.google.com/… or https://…"
                    className="rounded-sm"
                    data-testid="suggest-url"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Your name</label>
                    <Input
                      value={suggestForm.submitter_name}
                      onChange={(e) => setSuggestForm({ ...suggestForm, submitter_name: e.target.value })}
                      className="rounded-sm"
                      data-testid="suggest-name"
                    />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Email *</label>
                    <Input
                      type="email"
                      value={suggestForm.submitter_email}
                      onChange={(e) => setSuggestForm({ ...suggestForm, submitter_email: e.target.value })}
                      className="rounded-sm"
                      placeholder="you@company.com"
                      data-testid="suggest-email"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">Company (optional)</label>
                  <Input
                    value={suggestForm.submitter_company}
                    onChange={(e) => setSuggestForm({ ...suggestForm, submitter_company: e.target.value })}
                    className="rounded-sm"
                    data-testid="suggest-company"
                  />
                </div>
                <Button type="submit" disabled={suggestLoading} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="suggest-submit-btn">
                  {suggestLoading ? "Sending…" : (<><Send className="w-4 h-4 mr-2" /> Submit suggestion</>)}
                </Button>
                <p className="text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground text-center">
                  ◉ Reviewed within 48 hours
                </p>
              </form>
            </>
          ) : (
            <div className="py-8 text-center" data-testid="suggest-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <div className="font-display font-bold text-2xl mt-4">Submission received.</div>
              <p className="text-sm text-muted-foreground mt-3 max-w-sm mx-auto">
                Scott personally reviews every submission within 48 hours. If yours is approved,
                we&rsquo;ll credit you as the contributor and email you when it goes live.
              </p>
              <Button onClick={closeSuggest} className="mt-6 rounded-sm bg-primary hover:bg-primary/90" data-testid="suggest-close-btn">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ResourceCard({ r, large = false }) {
  return (
    <Link to={`/library/${r.slug}`} className="group" data-testid={`library-resource-${r.slug}`}>
      <div className={`h-full border border-border bg-card rounded-sm hover:border-primary hover:shadow-lg transition-all overflow-hidden`}>
        <div className={`relative h-32 bg-gradient-to-br from-primary/20 via-primary/5 to-accent/10 flex items-center justify-center border-b border-border`}>
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {r.skill_level && (
              <Badge className={`rounded-sm font-mono-plex text-[10px] uppercase tracking-widest ${SKILL_COLORS[r.skill_level] || ""}`}>
                {r.skill_level}
              </Badge>
            )}
          </div>
          <div className="absolute top-3 right-3">
            <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-card text-foreground border border-border">
              {r.resource_type}
            </Badge>
          </div>
          <BookOpen className="w-10 h-10 text-primary/40" />
        </div>
        <div className="p-5">
          <div className="font-display font-bold text-lg leading-tight group-hover:text-primary transition-colors">{r.title}</div>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3">{r.description}</p>
          <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-3 font-mono-plex uppercase tracking-widest">
              {r.time_saved && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.time_saved}</span>}
              <span className="inline-flex items-center gap-1"><Download className="w-3 h-3" /> {r.downloads_count || 0}</span>
            </div>
            <span className="text-primary group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
